import React from 'react';
import {
  List,
  Icon,
  Label,
  Divider,
  Message,
  Table,
  Header,
  Visibility,
  VisibilityEventData,
} from 'semantic-ui-react';
import { TocContext } from '../contexts/TocContext';
import rehypeReact from 'rehype-react';

import { getHashStr, customEncodeStr } from '../utils/hashutil';
import { relative } from 'path';

interface ComponentProps {
  children: React.Component;
}

const SemanticOL = ({ children }: ComponentProps): React.ReactNode => {
  return (
    <List relaxed ordered as="ol">
      {children}
    </List>
  );
};

const SemanticUL = ({ children }: ComponentProps): React.ReactNode => (
  <List relaxed bulleted as="ul">
    {children}
  </List>
);

const SemanticLI = ({ children }: ComponentProps): React.ReactNode => (
  <List.Item as="li">{children}</List.Item>
);

const MessageInfo = ({ children }: ComponentProps): React.ReactNode => (
  <Message icon>
    <Icon name="quote left" color="black" />
    <Message.Content>{children}</Message.Content>
  </Message>
);

const CustomTable = ({ children }: ComponentProps): React.ReactNode => (
  <Table
    celled
    compact
    // color="violet"
  >
    {children}
  </Table>
);

interface HeaderProps {
  id: string;
  children: {
    0: React.ReactNode;
    1: string;
  };
}

interface HandleProps extends VisibilityEventData {
  children: {
    props: {
      id: string;
    };
  };
}

// const HeaderX: React.ComponentType<any> = ({children,id}: any, heading: string) => {
const HeaderX = (element: HeaderProps, headingNum: number): React.ReactNode => {
  const { id, children } = element;
  const { func } = React.useContext(TocContext);

  const astr: string = id;
  const anchor: string = astr ? getHashStr(customEncodeStr(astr)) : '';
  // const headerStr = children.length > 1: children[1]: '';

  const leftMargin: number = (headingNum - 2) * 0.3;

  const handleTopPassed = (nothing: null, data: VisibilityEventData): void => {
    const concreteData: HandleProps = data as HandleProps;
    const passedId = concreteData.children.props.id;
    func(passedId);
  };

  const handleBottomPassedReserve = (e: null, data: VisibilityEventData) => {
    const concreteData: HandleProps = data as HandleProps;
    const passedId = concreteData.children.props.id;
    func(passedId);
  };

  return (
    <Visibility
      onTopPassed={handleTopPassed}
      onBottomPassedReverse={handleBottomPassedReserve}
      once={false}
      // onUpdate={handleUpdate}
      fireOnMount={true}
      offset={[15, 10]}
      updateOn={'repaint'}
    >
      <section
        id={anchor}
        style={{
          position: 'sticky',
          top: 0,
        }}
      >
        <Header
          // as={heading}
          as={`h${headingNum}`}
          // style={{marginTop: -60, paddingTop: 60}}
          style={{
            // background: '#fde',
            background: '#eef',
            paddingTop: 10,            
            paddingBottom: 5,
            paddingLeft: 5,
            marginLeft: `${leftMargin}rem`,
            marginTop: '1rem',
            marginBottom: '1rem',
            // borderLeft: `solid 4px #f94`,
            // borderBottom: `solid 2px #f94`,
            borderLeft: `solid 4px #003`,
            borderBottom: `solid 2px #003`,
            borderRadius: 3,
            position: 'static'
      }}
    >
          <a
            href={`#${anchor}`}
            // href={`#${anchor}`}
            aria-label={`${astr} permalink`}
            className="anchor"
            style={{
              position: 'relative',
              right: 10
            }}
          >
            <LinkMark />
          </a>
          {children[1]}
        </Header>
      </section>
    </Visibility>
  );
};

const Header1 = (element: HeaderProps): React.ReactNode =>
  HeaderX(element, 1);
const Header2 = (element: HeaderProps): React.ReactNode =>
  HeaderX(element, 2);
const Header3 = (element: HeaderProps): React.ReactNode =>
  HeaderX(element, 3);
const Header4 = (element: HeaderProps): React.ReactNode =>
  HeaderX(element, 4);
const Header5 = (element: HeaderProps): React.ReactNode =>
  HeaderX(element, 5);
const Header6 = (element: HeaderProps): React.ReactNode =>
  HeaderX(element, 6);

const LinkMark = (): React.FunctionComponentElement<{}> => (
  <svg
    aria-hidden="true"
    focusable="false"
    height="16"
    version="1.1"
    viewBox="0 0 16 16"
    width="16"
  >
    <path
      fillRule="evenodd"
      d="M4 9h1v1H4c-1.5 0-3-1.69-3-3.5S2.55 3 4 3h4c1.45 0 3 1.69 3 3.5 0 1.41-.91 2.72-2 3.25V8.59c.58-.45 1-1.27 1-2.09C10 5.22 8.98 4 8 4H4c-.98 0-2 1.22-2 2.5S3 9 4 9zm9-3h-1v1h1c1 0 2 1.22 2 2.5S13.98 12 13 12H9c-.98 0-2-1.22-2-2.5 0-.83.42-1.64 1-2.09V6.25c-1.09.53-2 1.84-2 3.25C6 11.31 7.55 13 9 13h4c1.45 0 3-1.69 3-3.5S14.5 6 13 6z"
    />
  </svg>
);

// This will render the htmlAst contents of a post
// using rehype, allowing us to replace elements with
// react components - we currently use this to style
// lists etc. as Semantic components
// See https://using-remark.gatsbyjs.org/custom-components/
export const renderAst = new rehypeReact({
  createElement: React.createElement,
  components: {
    ul: SemanticUL as React.ComponentType<any>,
    ol: SemanticOL as React.ComponentType<any>,
    li: SemanticLI as React.ComponentType<any>,
    h1: Header1 as React.ComponentType<any>,
    h2: Header2 as React.ComponentType<any>,
    h3: Header3 as React.ComponentType<any>,
    h4: Header4 as React.ComponentType<any>,
    h5: Header5 as React.ComponentType<any>,
    h6: Header6 as React.ComponentType<any>,
    hr: Divider as React.ComponentType<any>,
    icon: Icon as React.ComponentType<any>,
    label: Label as React.ComponentType<any>,
    // a: SmartLink,
    blockquote: MessageInfo as React.ComponentType<any>,
    message: Message as React.ComponentType<any>,
    table: CustomTable as React.ComponentType<any>,
    thead: Table.Header as React.ComponentType<any>,
    tr: Table.Row as React.ComponentType<any>,
    th: Table.HeaderCell as React.ComponentType<any>,
    td: Table.Cell as React.ComponentType<any>,
  },
}).Compiler;
